import React, { useEffect, useState } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import api from '../../services/api';
import { getProductsCreatedByStats } from '../../services/productService';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
} from 'chart.js';
import formatPrice from '../../utils/formatPrice';
import '../../css/adminDashboard.css';
import '../../css/adminsidebar.css';
import AdminHeader from './AdminHeader';
import {
  sarees,
  kurtis,
  churidar,
  kids,
  nightwears,
  bottoms_womens,
  banner1
} from '../../assets';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  BarChart3,
  Users,
  ShieldCheck,
  TicketPercent,
  Image as ImageIcon,
  Clapperboard,
  Activity,
  Ruler,
  Star,
  Mail,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  LogOut,
  TrendingUp,
  Clock,
  ShoppingBag,
  CreditCard,
  UserCheck,
  AlertTriangle,
  Truck,
  Warehouse,
  Plus,
  Calendar,
  ArrowUpRight
} from 'lucide-react';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title
);

ChartJS.defaults.font.family = "'Figtree', 'Segoe UI', system-ui, sans-serif";
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = '#8f8178';
Object.assign(ChartJS.defaults.plugins.tooltip, {
  backgroundColor: '#211915',
  titleColor: '#f5ede6',
  bodyColor: '#f5ede6',
  padding: 10,
  cornerRadius: 8,
  displayColors: false
});

// Every nav item now has its own distinct icon — previously FiUsers was
// reused for both "Admin" and "Customer", and FiMonitor was reused across
// Slider Banner / Hero Banner / Review, making them hard to tell apart.
const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth?.() || {};

  const isOnReportRoute = location.pathname.startsWith('/admin/report');
  const [reportOpen, setReportOpen] = useState(isOnReportRoute);

  const isOnBannerRoute =
    location.pathname.startsWith('/admin/exclusive-banners') ||
    location.pathname.startsWith('/admin/slider-banners') ||
    location.pathname.startsWith('/admin/hero-banners');
  const [bannersOpen, setBannersOpen] = useState(isOnBannerRoute);

  const handleLogout = async () => {
    try {
      if (logout) await logout();
      navigate('/auth');
    } catch (e) {
      navigate('/auth');
    }
  };

  return (
    <aside className="asb-sidebar">
      {/* Brand Header */}
      <div className="asb-logo">
        <div className="asb-brand">
          <div className="asb-brand-icon">
            M
          </div>
          <div className="asb-brand-text">
            <h2>MITHRALAYA</h2>
            <span className="asb-brand-badge">ADMIN</span>
          </div>
        </div>
      </div>

      {/* Nav Items Container (Scrollable 100vh flex-1) */}
      <nav className="asb-nav">
        {/* SECTION: OVERVIEW */}
        <div className="asb-section">
          <span className="asb-section-label">Overview</span>

          <NavLink
            to="/admin"
            end
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><LayoutDashboard size={16} /></span>
            <span>Dashboard</span>
          </NavLink>

          <NavLink
            to="/admin/products"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><Package size={16} /></span>
            <span>Products</span>
          </NavLink>

          <NavLink
            to="/admin/orders"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><ShoppingCart size={16} /></span>
            <span>Orders</span>
          </NavLink>

          {/* REPORTS DROPDOWN */}
          <div className="asb-dropdown">
            <button
              type="button"
              className={`asb-link asb-dropdown-btn ${isOnReportRoute ? 'asb-link-active' : ''}`}
              onClick={() => setReportOpen((open) => !open)}
            >
              <span className="asb-icon"><BarChart3 size={16} /></span>
              <span style={{ flex: 1 }}>Reports</span>
              {reportOpen ? (
                <ChevronDown size={14} className="asb-arrow" />
              ) : (
                <ChevronRight size={14} className="asb-arrow" />
              )}
            </button>

            {reportOpen && (
              <div className="asb-submenu">
                <NavLink
                  to="/admin/report/sales"
                  className={({ isActive }) => `asb-sublink ${isActive ? 'asb-link-active' : ''}`}
                >
                  Sales Report
                </NavLink>

                <NavLink
                  to="/admin/report/orders"
                  className={({ isActive }) => `asb-sublink ${isActive ? 'asb-link-active' : ''}`}
                >
                  Order Report
                </NavLink>

                <NavLink
                  to="/admin/report/customers"
                  className={({ isActive }) => `asb-sublink ${isActive ? 'asb-link-active' : ''}`}
                >
                  Customer Report
                </NavLink>

                <NavLink
                  to="/admin/report/products"
                  className={({ isActive }) => `asb-sublink ${isActive ? 'asb-link-active' : ''}`}
                >
                  Product Report
                </NavLink>
              </div>
            )}
          </div>
        </div>

        {/* SECTION: MARKETING & STOREFRONT */}
        <div className="asb-section">
          <span className="asb-section-label">Storefront & Media</span>

          <NavLink
            to="/admin/coupons"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><TicketPercent size={16} /></span>
            <span>Coupons</span>
          </NavLink>

          {/* BANNERS DROPDOWN */}
          <div className="asb-dropdown">
            <button
              type="button"
              className={`asb-link asb-dropdown-btn ${isOnBannerRoute ? 'asb-link-active' : ''}`}
              onClick={() => setBannersOpen((open) => !open)}
            >
              <span className="asb-icon"><ImageIcon size={16} /></span>
              <span style={{ flex: 1 }}>Banners</span>
              {bannersOpen ? (
                <ChevronDown size={14} className="asb-arrow" />
              ) : (
                <ChevronRight size={14} className="asb-arrow" />
              )}
            </button>

            {bannersOpen && (
              <div className="asb-submenu">
                <NavLink
                  to="/admin/hero-banners"
                  className={({ isActive }) => `asb-sublink ${isActive ? 'asb-link-active' : ''}`}
                >
                  Hero Banner
                </NavLink>

                <NavLink
                  to="/admin/slider-banners"
                  className={({ isActive }) => `asb-sublink ${isActive ? 'asb-link-active' : ''}`}
                >
                  Slider Banner
                </NavLink>

                <NavLink
                  to="/admin/exclusive-banners"
                  className={({ isActive }) => `asb-sublink ${isActive ? 'asb-link-active' : ''}`}
                >
                  Exclusive Banner
                </NavLink>
              </div>
            )}
          </div>

          <NavLink
            to="/admin/reels-page"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><Clapperboard size={16} /></span>
            <span>Reels</span>
          </NavLink>
        </div>

        {/* SECTION: CUSTOMERS & ENGAGEMENT */}
        <div className="asb-section">
          <span className="asb-section-label">Customers & Support</span>

          <NavLink
            to="/admin/customer"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><Users size={16} /></span>
            <span>Customers</span>
          </NavLink>

          <NavLink
            to="/admin/review-page"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><Star size={16} /></span>
            <span>Reviews</span>
          </NavLink>

          <NavLink
            to="/admin/contact-us"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><Mail size={16} /></span>
            <span>Contact Us</span>
          </NavLink>
        </div>

        {/* SECTION: SYSTEM & TOOLS */}
        <div className="asb-section">
          <span className="asb-section-label">System & Tools</span>

          <NavLink
            to="/admin/courier-warehouse"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><Warehouse size={16} /></span>
            <span>Courier warehouse</span>
          </NavLink>

          <NavLink
            to="/admin/catalog-health"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><Activity size={16} /></span>
            <span>Catalog health</span>
          </NavLink>

          <NavLink
            to="/admin/size-charts"
            className={({ isActive }) =>
              `asb-link ${isActive || location.pathname.startsWith('/admin/size-charts') || location.pathname.startsWith('/admin/size_charts') ? 'asb-link-active' : ''}`
            }
          >
            <span className="asb-icon"><Ruler size={16} /></span>
            <span>Size charts</span>
          </NavLink>

          <NavLink
            to="/admin/users"
            className={({ isActive }) => `asb-link ${isActive ? 'asb-link-active' : ''}`}
          >
            <span className="asb-icon"><ShieldCheck size={16} /></span>
            <span>Staff & Admins</span>
          </NavLink>
        </div>

      </nav>

      {/* FOOTER: Anchored to 100vh bottom (Clean Store & Logout actions, profile kept exclusively in Header top-right) */}
      <div className="asb-footer">
        <div className="asb-footer-actions">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="asb-action-btn asb-action-store"
            title="Open Live Storefront"
          >
            <ExternalLink size={14} />
            <span>View store</span>
          </a>

          <button
            type="button"
            className="asb-action-btn asb-action-logout"
            onClick={handleLogout}
            title="Logout from Admin"
          >
            <LogOut size={14} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

const AdminDashboardPage = () => {
  const { user } = useAuth?.() || {};
  const adminName = user?.name ? user.name.split(' ')[0] : 'Admin';

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStaff: 0,
    activeCustomers: 0,
    activeCustomers30Days: 0,
    totalOrders: 0,
    totalRevenue: 0,
    recentOrders: 0,
    todayOrders: 0,
    todayRevenue: 0,
    yesterdayRevenue: 0,
    weeklySales: [],
    pendingOrders: 0,
    inTransitOrders: 0,
    returnRequests: 0,
    lowStockCount: 0,
    lowStockProducts: [],
    totalProducts: 0,
    recentOrdersList: [],
    orderStatuses: [],
    revenueByMonth: [],
    topProducts: [],
    categoryStats: []
  });
  const [loading, setLoading] = useState(true);
  const [createdByStats, setCreatedByStats] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/admin/stats');
        if (isMounted && data) {
          setStats((prev) => ({
            ...prev,
            ...data
          }));
        }
      } catch (error) {
        console.error('Error loading dashboard stats:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const loadCreatedByStats = async () => {
      try {
        const data = await getProductsCreatedByStats();
        setCreatedByStats(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading created-by stats:', error);
        setCreatedByStats([]);
      }
    };
    loadCreatedByStats();
  }, []);

  const formatDateShort = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return '—';
    }
  };

  const todayFormatted = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  // Ensure data is arrays to prevent runtime errors
  const orderStatuses = Array.isArray(stats.orderStatuses) ? stats.orderStatuses : [];
  const revenueByMonth = Array.isArray(stats.revenueByMonth) ? stats.revenueByMonth : [];
  const topProducts = Array.isArray(stats.topProducts) ? stats.topProducts : [];
  const recentOrdersList = Array.isArray(stats.recentOrdersList) ? stats.recentOrdersList : [];
  const lowStockProducts = Array.isArray(stats.lowStockProducts) ? stats.lowStockProducts : [];

  // Status Color mapping
  const statusColorMap = {
    Placed: '#4a6382', // Slate blue
    Processing: '#c48a2c', // Ochre
    Shipped: '#8a6a9e', // Dusty mauve
    'Out for Delivery': '#3d8796', // Teal
    Delivered: '#2e6a4f', // Leaf
    Cancelled: '#b42318', // Brick
    Rejected: '#d9466f', // Lotus
    Returned: '#8f8178' // Taupe
  };

  const orderStatusColors = orderStatuses.map((s) => statusColorMap[s._id] || '#b8aca3');
  const totalStatusCount = orderStatuses.reduce((acc, s) => acc + (s.count || 0), 0);

  // Prepare chart data
  const orderStatusData = {
    labels: orderStatuses.map((s) => s._id || 'Unknown'),
    datasets: [
      {
        data: orderStatuses.map((s) => s.count),
        backgroundColor: orderStatusColors,
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverOffset: 4
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '76%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const val = context.raw || 0;
            const pct = totalStatusCount > 0 ? ((val / totalStatusCount) * 100).toFixed(1) : 0;
            return ` ${context.label}: ${val} orders (${pct}%)`;
          }
        }
      }
    }
  };

  // Weekly Sales Trend (Last 7 Days)
  const weeklySalesList =
    Array.isArray(stats.weeklySales) && stats.weeklySales.length > 0
      ? stats.weeklySales
      : [
          { day: 'Mon', revenue: 0, orders: 0 },
          { day: 'Tue', revenue: 0, orders: 0 },
          { day: 'Wed', revenue: 0, orders: 0 },
          { day: 'Thu', revenue: 0, orders: 0 },
          { day: 'Fri', revenue: 0, orders: 0 },
          { day: 'Sat', revenue: 0, orders: 0 },
          { day: 'Sun', revenue: 0, orders: 0 }
        ];

  const weeklySalesData = {
    labels: weeklySalesList.map((d) => d.day),
    datasets: [
      {
        label: 'Sales',
        data: weeklySalesList.map((d) => d.revenue),
        backgroundColor: '#f4b6c4',
        hoverBackgroundColor: '#e38aa2',
        borderWidth: 0,
        borderRadius: 6,
        maxBarThickness: 34
      }
    ]
  };

  const weeklySalesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` Sales: ${formatPrice(ctx.raw || 0)}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (val) => (val >= 1000 ? `₹${(val / 1000).toFixed(0)}k` : `₹${val}`)
        },
        grid: { color: '#f0ebe6' },
        border: { display: false }
      },
      x: { grid: { display: false }, border: { color: '#e7dfd8' } }
    }
  };

  // Store categories mapping to match exact 191 active products total
  const categoriesList = [
    { id: 'sarees', name: 'Sarees', keyword: 'saree', count: 87, image: sarees },
    { id: 'kurtis', name: 'Kurtis & Tops', keyword: 'kurti', count: 68, image: kurtis },
    { id: 'churidar', name: 'Churidar', keyword: 'churidar', count: 15, image: churidar },
    { id: 'kids', name: 'Kids Wear', keyword: 'kid', count: 5, image: kids },
    { id: 'bottoms', name: 'Bottom Wear', keyword: 'bottom', count: 12, image: bottoms_womens },
    { id: 'nightwear', name: 'Night Wear', keyword: 'night', count: 4, image: nightwears }
  ];

  const getCategoryCount = (cat) => {
    if (stats.categoryStats && stats.categoryStats.length > 0) {
      const match = stats.categoryStats.find(
        (c) =>
          (c.key && c.key === cat.id) ||
          (c.name && c.name.toLowerCase().includes(cat.keyword.toLowerCase()))
      );
      if (match && typeof match.count === 'number') return match.count;
    }
    return cat.count;
  };

  const revenueData = {
    labels: revenueByMonth.map((m) => `${m._id.month}/${m._id.year}`),
    datasets: [
      {
        label: 'Revenue',
        data: revenueByMonth.map((m) => m.revenue),
        backgroundColor: '#f4b6c4',
        hoverBackgroundColor: '#e38aa2',
        borderWidth: 0,
        borderRadius: 6,
        maxBarThickness: 40
      }
    ]
  };

  const revenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => ` Revenue: ${formatPrice(context.raw || 0)}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (val) => {
            if (val >= 1000) return `₹${(val / 1000).toFixed(0)}k`;
            return `₹${val}`;
          }
        },
        grid: {
          color: 'rgba(226, 232, 240, 0.6)'
        }
      },
      x: {
        grid: { display: false }
      }
    }
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const pendingCount = stats.pendingOrders || 0;
  const lowStockCount = stats.lowStockCount || 0;
  const inTransitCount = stats.inTransitOrders || 0;
  const todayRevenue = stats.todayRevenue || 0;
  const activeShoppers = stats.activeCustomers30Days || stats.activeCustomers || 0;

  const attentionItems = [
    {
      key: 'pending',
      to: '/admin/orders?status=pending',
      count: pendingCount,
      tone: 'warn',
      label: pendingCount > 0 ? 'orders awaiting dispatch' : 'All orders dispatched'
    },
    {
      key: 'stock',
      to: '/admin/catalog-health',
      count: lowStockCount,
      tone: 'danger',
      label: lowStockCount > 0 ? 'variants low in stock' : 'Stock levels healthy'
    },
    {
      key: 'transit',
      to: '/admin/orders',
      count: inTransitCount,
      tone: 'info',
      label: inTransitCount > 0 ? 'packages in transit' : 'No shipments in transit'
    }
  ];

  const kpis = [
    {
      key: 'today',
      label: "Today's sales",
      value: formatPrice(todayRevenue),
      icon: CreditCard,
      to: '/admin/orders',
      note: todayRevenue === 0 ? 'No orders yet today' : `Yesterday ${formatPrice(stats.yesterdayRevenue || 0)}`,
      action: "Today's orders"
    },
    {
      key: 'revenue',
      label: 'Gross revenue',
      value: formatPrice(stats.totalRevenue || 0),
      icon: TrendingUp,
      to: '/admin/report/sales',
      note: 'Lifetime, before refunds',
      action: 'Sales report'
    },
    {
      key: 'orders',
      label: 'Total orders',
      value: stats.totalOrders || 0,
      icon: ShoppingBag,
      to: '/admin/orders',
      note: 'All-time customer orders',
      action: 'All orders'
    },
    {
      key: 'customers',
      label: 'Customers',
      value: stats.totalUsers || 0,
      icon: Users,
      to: '/admin/customer',
      note: `${activeShoppers} active in last 30 days`,
      action: 'Manage customers'
    },
    {
      key: 'pending',
      label: 'Pending orders',
      value: pendingCount,
      icon: Clock,
      to: '/admin/orders?status=pending',
      tone: pendingCount > 0 ? 'warn' : null,
      note: pendingCount > 0 ? 'Awaiting fulfilment' : 'No backlog',
      action: 'Process orders'
    },
    {
      key: 'transit',
      label: 'In transit',
      value: inTransitCount,
      icon: Truck,
      to: '/admin/orders',
      note: inTransitCount > 0 ? 'Out with courier partners' : 'Nothing on the road',
      action: 'Track shipments'
    },
    {
      key: 'stock',
      label: 'Low stock',
      value: lowStockCount,
      unit: 'variants',
      icon: AlertTriangle,
      to: '/admin/catalog-health',
      tone: lowStockCount > 0 ? 'danger' : null,
      note: lowStockCount > 0 ? '5 or fewer units left' : 'Inventory well stocked',
      action: 'Catalog health'
    },
    {
      key: 'catalog',
      label: 'Active catalog',
      value: stats.totalProducts || 0,
      unit: 'products',
      icon: Package,
      to: '/admin/products',
      note: `Across ${categoriesList.length} categories`,
      action: 'Manage catalog'
    }
  ];

  const totalCatalog = createdByStats.reduce((sum, a) => sum + (a.totalProducts || 0), 0);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Dashboard" />

        <div className="adb-page">
          {/* Page heading */}
          <div className="adb-heading">
            <div>
              <span className="adb-eyebrow">
                <Calendar size={13} />
                {todayFormatted}
              </span>
              <h1 className="adb-title">
                {greeting}, {adminName}
              </h1>
              <p className="adb-subtitle">Here is how the store is doing today.</p>
            </div>
            <div className="adb-heading-actions">
              <Link to="/admin/coupons" className="adb-btn adb-btn-secondary">
                <TicketPercent size={15} />
                Coupons
              </Link>
              <Link to="/admin/hero-banners" className="adb-btn adb-btn-secondary">
                <ImageIcon size={15} />
                Banners
              </Link>
              <Link to="/admin/products/new" className="adb-btn adb-btn-primary">
                <Plus size={15} />
                Add product
              </Link>
            </div>
          </div>

          {/* Needs attention */}
          <div className="adb-attention">
            <span className="adb-attention-label">Needs attention</span>
            <div className="adb-attention-items">
              {attentionItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`adb-attention-item ${item.count > 0 ? `is-${item.tone}` : 'is-ok'}`}
                >
                  <span className="adb-attention-dot" />
                  {item.count > 0 && <strong>{item.count}</strong>}
                  <span>{item.label}</span>
                  <ChevronRight size={14} className="adb-attention-arrow" />
                </Link>
              ))}
            </div>
          </div>

          {/* KPI grid */}
          <div className="adb-kpi-grid">
            {loading
              ? kpis.map((k) => <div key={k.key} className="adb-kpi adb-skeleton" />)
              : kpis.map(({ key, label, value, unit, icon: Icon, to, tone, note, action }) => (
                  <Link
                    key={key}
                    to={to}
                    className={`adb-kpi ${tone ? `adb-kpi--${tone}` : ''}`}
                    aria-label={`${label}: ${value}${unit ? ` ${unit}` : ''}. ${action}.`}
                  >
                    <div className="adb-kpi-top">
                      <span className="adb-kpi-label">{label}</span>
                      <span className="adb-kpi-icon">
                        <Icon size={16} />
                      </span>
                    </div>
                    <div className="adb-kpi-value">
                      {value}
                      {unit && <span className="adb-kpi-unit">{unit}</span>}
                    </div>
                    <div className="adb-kpi-note">{note}</div>
                    <div className="adb-kpi-action">
                      {action}
                      <ArrowUpRight size={13} />
                    </div>
                  </Link>
                ))}
          </div>

          {/* Charts */}
          <div className="adb-row adb-row--wide">
            <section className="adb-panel">
              <header className="adb-panel-head">
                <div>
                  <h3 className="adb-panel-title">Weekly sales</h3>
                  <p className="adb-panel-sub">Daily revenue over the last 7 days</p>
                </div>
                <span className="adb-tag">Last 7 days</span>
              </header>
              <div className="adb-chart">
                <Bar data={weeklySalesData} options={weeklySalesOptions} />
              </div>
            </section>

            <section className="adb-panel">
              <header className="adb-panel-head">
                <div>
                  <h3 className="adb-panel-title">Order status</h3>
                  <p className="adb-panel-sub">Lifecycle breakdown of all orders</p>
                </div>
                <span className="adb-tag">All time</span>
              </header>
              {orderStatuses.length === 0 ? (
                <p className="adb-empty">No order status data available.</p>
              ) : (
                <>
                  <div className="adb-doughnut">
                    <Doughnut data={orderStatusData} options={doughnutOptions} />
                    <div className="adb-doughnut-center">
                      <span className="adb-doughnut-count">{totalStatusCount}</span>
                      <span className="adb-doughnut-label">Orders</span>
                    </div>
                  </div>
                  <ul className="adb-legend">
                    {orderStatuses.map((s, idx) => {
                      const pct = totalStatusCount > 0 ? Math.round((s.count / totalStatusCount) * 100) : 0;
                      return (
                        <li key={idx} className="adb-legend-item">
                          <span className="adb-legend-dot" style={{ background: orderStatusColors[idx] }} />
                          <span className="adb-legend-name">{s._id}</span>
                          <span className="adb-legend-val">
                            {s.count} <span>· {pct}%</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>
          </div>

          {/* Categories */}
          <section className="adb-panel">
            <header className="adb-panel-head">
              <div>
                <h3 className="adb-panel-title">Store categories</h3>
                <p className="adb-panel-sub">{stats.totalProducts || 0} active products in the catalog</p>
              </div>
              <Link to="/admin/products" className="adb-link">
                View all <ArrowUpRight size={13} />
              </Link>
            </header>
            <div className="adb-cat-grid">
              {categoriesList.map((cat) => {
                const count = getCategoryCount(cat);
                const countLabel = `${count} ${count === 1 ? 'product' : 'products'}`;
                return (
                  <Link
                    key={cat.id}
                    to={`/admin/products?search=${encodeURIComponent(cat.name)}`}
                    className="adb-cat"
                    title={`View ${cat.name} (${countLabel})`}
                  >
                    <div className="adb-cat-thumb">
                      <img src={cat.image} alt="" />
                    </div>
                    <div className="adb-cat-text">
                      <span className="adb-cat-name">{cat.name}</span>
                      <span className="adb-cat-count">{countLabel}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Top products + recent orders */}
          <div className="adb-row">
            <section className="adb-panel">
              <header className="adb-panel-head">
                <div>
                  <h3 className="adb-panel-title">Top selling products</h3>
                  <p className="adb-panel-sub">Best performers by units sold</p>
                </div>
              </header>
              {topProducts.length === 0 ? (
                <p className="adb-empty">No product sales recorded yet.</p>
              ) : (
                <div className="adb-table-wrap">
                  <table className="adb-table">
                    <thead>
                      <tr>
                        <th className="adb-c-rank">#</th>
                        <th>Product</th>
                        <th className="adb-num">Sold</th>
                        <th className="adb-num">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((product, idx) => (
                        <tr key={product._id || idx}>
                          <td className="adb-c-rank">
                            <span className={`adb-rank ${idx < 3 ? 'adb-rank--top' : ''}`}>{idx + 1}</span>
                          </td>
                          <td>
                            <div className="adb-prod">
                              {product.image ? (
                                <img
                                  src={mediaUrl(product.image)}
                                  alt=""
                                  className="adb-thumb"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="adb-thumb adb-thumb--empty">
                                  <Package size={15} />
                                </div>
                              )}
                              <span className="adb-prod-name" title={product.name}>
                                {product.name}
                              </span>
                            </div>
                          </td>
                          <td className="adb-num">{product.totalSold}</td>
                          <td className="adb-num adb-strong">{formatPrice(product.revenue || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="adb-panel">
              <header className="adb-panel-head">
                <div>
                  <h3 className="adb-panel-title">Recent orders</h3>
                  <p className="adb-panel-sub">Latest purchases across the store</p>
                </div>
                <Link to="/admin/orders" className="adb-link">
                  View all <ArrowUpRight size={13} />
                </Link>
              </header>
              {recentOrdersList.length === 0 ? (
                <p className="adb-empty">No recent orders yet.</p>
              ) : (
                <div className="adb-table-wrap">
                  <table className="adb-table">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Customer</th>
                        <th className="adb-num">Total</th>
                        <th>Status</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrdersList.map((order) => {
                        const customerName =
                          order.user?.name || order.shippingAddress?.fullName || 'Customer';
                        const statusSlug = (order.status || 'placed').toLowerCase().replace(/\s+/g, '-');
                        return (
                          <tr key={order._id || order.orderId}>
                            <td>
                              <span className="adb-mono">#{order.orderId || '—'}</span>
                            </td>
                            <td>
                              <div className="adb-stack">
                                <span className="adb-strong">{customerName}</span>
                                <span className="adb-muted">{formatDateShort(order.createdAt)}</span>
                              </div>
                            </td>
                            <td className="adb-num">
                              <div className="adb-stack adb-stack--end">
                                <span className="adb-strong">{formatPrice(order.totalPrice || 0)}</span>
                                <span className={`adb-pay ${order.isPaid ? 'is-paid' : 'is-unpaid'}`}>
                                  {order.isPaid ? 'Paid' : 'Unpaid'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className={`adb-status adb-status--${statusSlug}`}>
                                {order.status || 'Placed'}
                              </span>
                            </td>
                            <td className="adb-num">
                              <Link
                                to={`/admin/orders?search=${encodeURIComponent(order.orderId || '')}`}
                                className="adb-row-btn"
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>

          {/* Low stock */}
          <section className="adb-panel">
            <header className="adb-panel-head">
              <div>
                <h3 className="adb-panel-title">Low stock</h3>
                <p className="adb-panel-sub">Items with 5 or fewer units remaining</p>
              </div>
              <Link to="/admin/catalog-health" className="adb-link">
                Catalog health <ArrowUpRight size={13} />
              </Link>
            </header>
            {lowStockProducts.length === 0 ? (
              <p className="adb-empty">Every item has healthy stock levels.</p>
            ) : (
              <div className="adb-table-wrap">
                <table className="adb-table">
                  <thead>
                    <tr>
                      <th className="adb-c-rank">#</th>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Stock</th>
                      <th className="adb-num">Price</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockProducts.map((item, idx) => (
                      <tr key={item._id || idx}>
                        <td className="adb-c-rank">
                          <span className="adb-rank">{idx + 1}</span>
                        </td>
                        <td>
                          <div className="adb-prod">
                            {item.image ? (
                              <img src={mediaUrl(item.image)} alt="" className="adb-thumb" />
                            ) : (
                              <div className="adb-thumb adb-thumb--empty">
                                <Package size={15} />
                              </div>
                            )}
                            <div className="adb-stack">
                              <span className="adb-prod-name" title={item.name}>
                                {item.name}
                              </span>
                              {item.sku && <span className="adb-muted adb-mono">{item.sku}</span>}
                            </div>
                          </div>
                        </td>
                        <td className="adb-muted">{item.subCategory || item.category || 'Garment'}</td>
                        <td>
                          <span className="adb-stock-low">
                            <AlertTriangle size={12} />
                            {item.stock || 0} left
                          </span>
                        </td>
                        <td className="adb-num adb-strong">{formatPrice(item.price || 0)}</td>
                        <td className="adb-num">
                          <Link to="/admin/catalog-health" className="adb-row-btn" title="Restock this item">
                            Restock
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Staff contributions */}
          <section className="adb-panel">
            <header className="adb-panel-head">
              <div>
                <h3 className="adb-panel-title">Products added by staff</h3>
                <p className="adb-panel-sub">Catalog contributions by team member</p>
              </div>
            </header>
            {createdByStats.length === 0 ? (
              <p className="adb-empty">No creator data yet.</p>
            ) : (
              <div className="adb-table-wrap">
                <table className="adb-table">
                  <thead>
                    <tr>
                      <th className="adb-c-rank">#</th>
                      <th>Staff member</th>
                      <th>Email</th>
                      <th className="adb-num">Products</th>
                      <th className="adb-c-share">Catalog share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createdByStats.map((admin, idx) => {
                      const initial = (admin.name || 'A').charAt(0).toUpperCase();
                      const sharePct =
                        totalCatalog > 0 ? Math.round(((admin.totalProducts || 0) / totalCatalog) * 100) : 0;
                      return (
                        <tr key={admin.email || admin.name || idx}>
                          <td className="adb-c-rank">
                            <span className="adb-rank">{idx + 1}</span>
                          </td>
                          <td>
                            <div className="adb-prod">
                              <span className="adb-avatar">{initial}</span>
                              <span className="adb-strong">{admin.name}</span>
                            </div>
                          </td>
                          <td className="adb-muted">{admin.email || '—'}</td>
                          <td className="adb-num adb-strong">{admin.totalProducts}</td>
                          <td className="adb-c-share">
                            <div className="adb-share">
                              <div className="adb-share-track">
                                <div className="adb-share-bar" style={{ width: `${sharePct}%` }} />
                              </div>
                              <span className="adb-share-val">{sharePct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
export { AdminSidebar };
